import { Injectable } from '@angular/core';
import { commonConstants } from '../../../../CONSTANTS/tpi-common-constants';
import { CoveragesSelectedValue } from '../../../../tpi-models/general/coverages-models';
import { AgentDetails } from '../../../../tpi-models/general/agent-information';
import { DistributionPartnerAPI } from '@nationwide/internet-servicing-dpim-requests';

@Injectable({
    providedIn: 'root'
})

export class SearchResultsAdapterService {

    adaptToDpimContactInformation(dpimResponse: any, producerNumber?: string): AgentDetails {
        const result: AgentDetails = {
            phones: []
        };

        // Extract name information
        const roleName = dpimResponse?.roleNames?.[0];
        if (roleName?.personName) {
            result.firstName = roleName.personName.firstName;
            result.lastName = roleName.personName.lastName;
            result.middleName = roleName.personName.middleName;
        } else if (roleName?.orgName) {
            result.organization = roleName.orgName;
        }

        // Extract contact points information
        if (dpimResponse?.roleContactPoints) {
            for (const roleContactPoint of dpimResponse.roleContactPoints) {
                const contactPoint = roleContactPoint.contactPoint;

                if (contactPoint?.postalAddress) {
                    result.addressLineOne = contactPoint.postalAddress.addressLineOne;
                    result.city = contactPoint.postalAddress.city;
                    result.state = contactPoint.postalAddress.stateOrProvince;
                    result.zip = this.formatZip(contactPoint.postalAddress.postalCode);
                } else if (contactPoint?.email?.emailAddress) {
                    result.email = contactPoint.email.emailAddress;
                } else if (contactPoint?.phone?.fullPhoneNumber) {
                    result.phones.push(contactPoint.phone.fullPhoneNumber);
                }
            }
        }

        // Check SA360 membership if producerNumber is provided
        if (producerNumber && dpimResponse) {
            result.hasActiveSA360Membership = this.checkSA360Membership(dpimResponse, producerNumber);
        }

        return result;
    }

    isPolicyLevelCoverageAndSelected(coverage: any, vehicleId: number): boolean {
        const selectedValueFiltered = [];
        for (const selectedValue of coverage.selectedValue) {
            if (selectedValue.value !== commonConstants.FALSE_STRING && (
                coverage.coverageLevel === commonConstants.POLICY ||
                coverage.coverageLevel === commonConstants.VEHICLE &&
                coverage.coverableId === vehicleId
            )) {
                selectedValueFiltered.push(this.mapSelectedValueDescription(selectedValue));
            }
        }
        if (selectedValueFiltered.length > 0) {
            coverage.selectedValue = selectedValueFiltered;
            return true;
        }
        return false;
    }

    mapSelectedValueDescription(selectedValue: CoveragesSelectedValue): CoveragesSelectedValue {
        if (selectedValue.code === commonConstants.COVERAGES.FULL_SAFETY_GLASS_CODE) {
            selectedValue.description = commonConstants.COVERAGES.FULL_SAFETY_GLASS_DESCRIPTION;
        }
        return selectedValue;
    }

    private checkSA360Membership(distributionPartnerRoleResponse: DistributionPartnerAPI.DpimRoleInfoResponse, producerNumber: string): boolean {
        // Try AMF (Agent Management Framework) path first
        let activeSA360Addendum = this.getContractAddendumsAMF(distributionPartnerRoleResponse, producerNumber);
        // If not found in AMF, try PDS (Policy Distribution System) path
        if (!activeSA360Addendum) {
            activeSA360Addendum = this.getContractAddendumsPDS(distributionPartnerRoleResponse);
        }
        return !!activeSA360Addendum;
    }

    private formatZip(postalCode?: string): string | undefined {
        if (!postalCode) {
            return undefined;
        }
        const zipLength = 5;
        return postalCode.length > zipLength ?
            `${postalCode.slice(0, Math.max(0, zipLength))}-${postalCode.slice(Math.max(0, zipLength))}` :
            postalCode;
    }

    private getContractAddendumsAMF(distributionPartnerRoleResponse: DistributionPartnerAPI.DpimRoleInfoResponse, producerNumber: string): any {
        const subRole = distributionPartnerRoleResponse?.subRoles?.filter((subRoleInner) => subRoleInner.sourceIdentifier === producerNumber)[0];
        let sa360ContractAddendums = null;
        if (subRole?.subRoleContractAddendums) {
            sa360ContractAddendums = subRole.subRoleContractAddendums.filter((contractAddendum) => contractAddendum.addendumType.includes('Service Alliance 360'));
        }

        const activeSA360Addendum = sa360ContractAddendums?.find((addendum) => this.isAgentSA360MembershipActive(addendum));
        return activeSA360Addendum;
    }

    private getContractAddendumsPDS(distributionPartnerRoleResponse: DistributionPartnerAPI.DpimRoleInfoResponse): any {
        const sa360Addendums = distributionPartnerRoleResponse?.contractSummary?.contractAddendums?.filter((contractAddendum) => contractAddendum.addendumType.includes('Service Alliance 360'));
        const activeSA360Addendum = sa360Addendums?.find((addendum) => this.isAgentSA360MembershipActive(addendum));
        return activeSA360Addendum;
    }

    private getExpiryDate(contractAddendums: any): string {
        let firstSpaceIndex: number;
        let expiryDate;
        if (contractAddendums.expiryDate) {
            const expiryDateOriginal: string = contractAddendums.expiryDate;
            if (expiryDateOriginal.includes(' ')) {
                firstSpaceIndex = expiryDateOriginal.indexOf(' ');
                expiryDate = expiryDateOriginal.slice(0, firstSpaceIndex);
            } else {
                expiryDate = expiryDateOriginal;
            }
        }
        return expiryDate;
    }

    private isAgentSA360MembershipActive(contractAddendums: any): boolean {
        let isSA360MembershipActive = false;
        const expiryDate = this.getExpiryDate(contractAddendums);
        if (expiryDate) {
            if (new Date(expiryDate) > new Date()) {
                isSA360MembershipActive = true;
            }
        } else {
            isSA360MembershipActive = true;
        }
        return isSA360MembershipActive;
    }

}